<script setup>
import { computed, defineAsyncComponent, inject, ref } from 'vue';
import { useRoute } from 'vue-router';

import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';
import CopilotLauncher from 'dashboard/components-next/copilot/CopilotLauncher.vue';
import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import { useCallsStore } from 'dashboard/stores/calls';
import { isUpgradePageBypassRoute } from 'dashboard/helper/routeHelpers';

/**
 * Everything the router renders, plus the app-level overlays that have to
 * outlive a route change. This is the half of the dashboard that does not
 * change when the chrome around it does, so both the stock layout and the
 * Meridian shell mount exactly this.
 */
defineProps({
  showCreateAccountModal: { type: Boolean, default: false },
  showShortcutModal: { type: Boolean, default: false },
  /**
   * The content surface, supplied by whichever chrome is hosting us.
   *
   * This has to come from the host rather than being hardcoded here: the
   * component is mounted by both layouts, so a Meridian token baked in at this
   * level would repaint the *stock* layout too — and once the Meridian palette
   * stops being declared at `:root` (DEV-419), it would resolve to an
   * undefined custom property and the stock background would fail outright.
   * The default is upstream Chatwoot's own value, so a detached install is
   * correct without the gate having to say anything.
   */
  surfaceClass: { type: String, default: 'bg-n-surface-1' },
  /**
   * Whether this host is responsible for mounting Copilot.
   *
   * `CopilotContainer` self-gates on the `is_copilot_panel_open` UI setting, so
   * two mounted instances both render once that flag is on. The Meridian
   * details panel hosts Copilot inside its own tab and therefore turns this
   * off; the stock layout keeps it.
   */
  hostsCopilot: { type: Boolean, default: true },
});

const emit = defineEmits(['closeAccountCreateModal', 'closeKeyShortcutModal']);

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);
const FloatingCallWidget = defineAsyncComponent(
  () => import('dashboard/components-next/call/FloatingCallWidget.vue')
);

const route = useRoute();
const callsStore = useCallsStore();

/**
 * Set while the Meridian settings sheet is drawing one of its own pages over
 * the current route (provided by `MeridianSettingsSheet`, which keeps this
 * component mounted for the overlays above). The routed screen would render
 * unseen underneath it, fetching data nobody is looking at, so it is left
 * unrendered instead. Nothing provides this in the stock layout, and the
 * default keeps a detached install on the RouterView unconditionally.
 */
const meridianSettingsPage = inject('meridianSettingsPageActive', null);
const suppressRoutedScreen = computed(() =>
  Boolean(meridianSettingsPage?.value)
);

const upgradePageRef = ref(null);

const hasActiveCall = computed(() => callsStore.hasActiveCall);
const hasIncomingCall = computed(() => callsStore.hasIncomingCall);

const showUpgradePage = computed(
  () => upgradePageRef.value?.shouldShowUpgradePage
);

// v4.17.0: the command bar stays mounted behind the paywall and is told to
// present itself as paywalled, rather than disappearing with the routed screen.
const isAccountPaywalled = computed(
  () => upgradePageRef.value?.isAccountPaywalled
);

// v4.17.0 moved this list into a shared helper; read it rather than keeping a
// second copy, or the two drift the next time upstream adds a bypass route.
const bypassUpgradePage = computed(() => isUpgradePageBypassRoute(route.name));
</script>

<template>
  <main
    class="flex flex-1 w-full h-full min-h-0 px-0 overflow-hidden"
    :class="surfaceClass"
  >
    <UpgradePage
      v-show="showUpgradePage"
      ref="upgradePageRef"
      :bypass-upgrade-page="bypassUpgradePage"
    >
      <slot name="mobile-sidebar-launcher" />
    </UpgradePage>
    <template v-if="!showUpgradePage">
      <RouterView v-if="!suppressRoutedScreen" />
      <CopilotLauncher v-if="hostsCopilot" />
      <slot name="mobile-sidebar-launcher" />
      <CopilotContainer v-if="hostsCopilot" />
      <FloatingCallWidget v-if="hasActiveCall || hasIncomingCall" />
    </template>
    <CommandBar :is-paywalled="isAccountPaywalled" />
    <AddAccountModal
      :show="showCreateAccountModal"
      @close-account-create-modal="emit('closeAccountCreateModal')"
    />
    <WootKeyShortcutModal
      :show="showShortcutModal"
      @close="emit('closeKeyShortcutModal')"
      @clickaway="emit('closeKeyShortcutModal')"
    />
  </main>
</template>
